import type { ExecutionStatus } from '@mct/shared';

import { SIGNAL_LABEL } from '../components/DagView/signal';

/**
 * Single source of truth for the status → pill style mapping.
 *
 * The product moved from a "● ラベル" (colored dot + text) treatment to
 * rendering the status word itself as a tinted label/pill (緑 完了 /
 * グレー 未着手 / 黄 進行中 / 赤 ブロック / 薄グレー スキップ). Every
 * surface — detail panel, list, DAG cards, dashboard legend, filter
 * chips — pulls its colors from here so the color language never drifts.
 *
 * Tints stay light (`bg-*-100 text-*-700`) so the label reads as a quiet
 * badge rather than a loud solid fill.
 */
const STATUS_PILL: Record<ExecutionStatus, { bg: string; text: string }> = {
  not_started: { bg: 'bg-slate-100', text: 'text-slate-700' },
  in_progress: { bg: 'bg-amber-100', text: 'text-amber-800' },
  blocked: { bg: 'bg-red-100', text: 'text-red-700' },
  done: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  // Skipped is intentionally the most muted — it's "N/A for this run".
  skipped: { bg: 'bg-slate-50', text: 'text-slate-400' },
};

export interface StatusPillStyle {
  /** Background tint utility, e.g. `bg-emerald-100`. */
  bg: string;
  /** Foreground text utility, e.g. `text-emerald-700`. */
  text: string;
  /** Localised status label, e.g. `完了`. */
  label: string;
}

/** Full `{ bg, text, label }` descriptor for a status. */
export function statusPillStyle(status: ExecutionStatus): StatusPillStyle {
  const c = STATUS_PILL[status];
  return { bg: c.bg, text: c.text, label: SIGNAL_LABEL[status] };
}

/**
 * Combined `bg + text` className for a read-only status pill. Pair with
 * shared shape utilities (`rounded-full px-2 py-0.5 text-xs`) at the call
 * site, or just render {@link StatusBadge}.
 */
export function statusPillClass(status: ExecutionStatus): string {
  const c = STATUS_PILL[status];
  return `${c.bg} ${c.text}`;
}
